/**
 * useTilePrewarmEffect — ADR-037 Phase 1, pre-warm B (client-side).
 *
 * Runs ONCE per app session, on a NON-map screen (mounted in AppShell right
 * after /auth/me succeeds), to warm the user's likely-first-view NDVI tiles in
 * the background so the first «Карта» click paints from cache.
 *
 * What it does, in order:
 *   1. Bail immediately on Save-Data / slow connection (politeness).
 *   2. On browser idle (requestIdleCallback, setTimeout fallback) — so it never
 *      competes with dashboard render — fetch:
 *        - the user's first farm + its bbox from the SHARED /map-init cache
 *          (getMapInit() — same module the /map page reads, no extra request if
 *          AppShell already prefetched it), and
 *        - the latest available NDVI date for that farm from /satellite
 *          available-dates (the existing scope-aware endpoint).
 *   3. Hand both to warmFirstView(), which builds the SAME tile URLs the live
 *      MapLibre source would (buildRasterUrl) and fires detached, low-priority
 *      `new Image()` GETs for the bounded farm-view tile set (≤ PREWARM_TILE_CAP
 *      tiles). It NEVER touches a live MapLibre source (AK-119-safe).
 *
 * Bounded & polite:
 *   - one farm × one latest date × one zoom × NDVI only (warmFirstView caps it);
 *   - debounced behind requestIdleCallback;
 *   - module-level dedupe in tilePrewarm so re-mounts don't re-warm;
 *   - skipped entirely on Save-Data / slow net / no farms.
 *
 * Failure isolation: every step is wrapped — a 401/timeout/parse error just
 * means "no warm", never a thrown error in AppShell.
 */
import { useEffect } from 'react';
import { getMapInit } from './mapInitCache';
import { warmFirstView, shouldSkipForConnection } from './tilePrewarm';

/** Latest ISO date (lexicographic sort works for YYYY-MM-DD) or null. */
function latestOf(dates: string[] | undefined | null): string | null {
  if (!dates || dates.length === 0) return null;
  let max = dates[0]!;
  for (const d of dates) if (d > max) max = d;
  return max;
}

/**
 * Fetch the latest available NDVI date for a farm via the existing scope-aware
 * endpoint (same one useAvailableDates uses). Kept inline (not the hook) because
 * we run outside React render and want a one-shot promise. Returns null on any
 * failure — warming is best-effort.
 */
async function fetchLatestNdviDate(farmId: string): Promise<string | null> {
  try {
    const params = new URLSearchParams({ index_code: 'ndvi', farm_id: farmId });
    const r = await fetch(`/api/v1/ndvi/available-dates?${params.toString()}`, {
      credentials: 'include',
    });
    if (!r.ok) return null;
    const data = (await r.json()) as { dates?: string[] };
    return latestOf(data?.dates);
  } catch {
    return null;
  }
}

/** Schedule fn on browser idle; setTimeout fallback for Safari < 16. */
function onIdle(fn: () => void): () => void {
  const ric = (window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    cancelIdleCallback?: (h: number) => void;
  }).requestIdleCallback;
  if (ric) {
    const handle = ric(fn, { timeout: 3000 });
    return () => {
      (window as Window & { cancelIdleCallback?: (h: number) => void }).cancelIdleCallback?.(
        handle,
      );
    };
  }
  const t = window.setTimeout(fn, 600);
  return () => window.clearTimeout(t);
}

/**
 * Mount-once tile pre-warm. Pass `enabled` (e.g. `!!me` post-login) so we only
 * warm for an authenticated user on a real app screen.
 */
export function useTilePrewarmEffect(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    if (shouldSkipForConnection()) return;

    let cancelled = false;

    const cancelIdle = onIdle(() => {
      void (async () => {
        try {
          // Shared /map-init cache — no extra request if AppShell already
          // prefetched it. We only need the first farm marker (id + bbox).
          const init = await getMapInit();
          if (cancelled) return;
          const firstFarm = init.markers?.[0];
          if (!firstFarm || !firstFarm.bbox) return; // no farms / no geometry

          const latestDate = await fetchLatestNdviDate(firstFarm.id);
          if (cancelled || !latestDate) return;

          warmFirstView({
            farm: { id: firstFarm.id, bbox: firstFarm.bbox },
            latestDate,
          });
        } catch {
          /* best-effort — never surface a warming error */
        }
      })();
    });

    return () => {
      cancelled = true;
      cancelIdle();
    };
  }, [enabled]);
}
